import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useContent } from "@/context/ContentContext";
import { paragraphs } from "@/lib/api";
import useMeta from "@/hooks/useMeta";
import { Reveal } from "@/components/site/Reveal";
import { Portrait } from "@/components/site/Portrait";
import { CtaBand } from "@/components/site/CtaBand";
import { ServiceCards } from "@/components/site/ServiceCards";

const Hero = ({ h }) => (
  <section className="relative overflow-hidden">
    <div aria-hidden="true" className="pointer-events-none absolute -left-40 top-20 h-[460px] w-[460px] rounded-full bg-[#B9A9D8]/20 blur-3xl" />
    <div className="wrap relative grid items-center gap-12 pb-20 pt-14 sm:pt-20 lg:grid-cols-[1.25fr_1fr] lg:gap-16 lg:pb-28">
      <Reveal>
        <p className="eyebrow mb-6" data-testid="hero-eyebrow">{h.eyebrow}</p>
        <h1 className="text-4xl leading-[1.08] text-[#22304A] sm:text-5xl lg:text-6xl" data-testid="hero-headline">{h.headline}</h1>
        <p className="mt-7 max-w-xl text-base leading-relaxed text-[#22304A]/80 md:text-lg" data-testid="hero-subhead">{h.subhead}</p>
        <div className="mt-10 flex flex-col flex-wrap gap-3 sm:flex-row">
          <Link to="/book" className="btn-primary" data-testid="hero-primary-cta">{h.cta_primary} <ArrowRight className="h-4 w-4" aria-hidden="true" /></Link>
        </div>
      </Reveal>
      <Reveal delay={200} className="relative mx-auto w-full max-w-[380px] lg:max-w-none">
        <div className="relative aspect-[4/5] overflow-hidden rounded-t-[999px] rounded-b-[32px]">
          <img src="/botanical-hero.webp" alt="Soft-focus lavender stems against a pale violet background" width="1067" height="1600" fetchpriority="high" className="h-full w-full object-cover" />
        </div>
        <img src="/flower-mark.png" alt="" aria-hidden="true" width="128" height="128" className="absolute -bottom-8 -left-6 h-28 w-28 rounded-full border border-[#B9A9D8]/40 bg-white p-2 sm:h-32 sm:w-32" />
      </Reveal>
    </div>
  </section>
);

const Intro = ({ h }) => (
  <section className="wrap py-16 sm:py-24">
    <Reveal className="grid gap-8 md:grid-cols-[1fr_1.4fr] md:gap-16">
      <h2 className="text-3xl leading-tight text-[#22304A] sm:text-4xl lg:text-[44px]" data-testid="intro-title">{h.intro_title}</h2>
      <div className="space-y-5 text-base leading-[1.85] text-[#22304A]/80 md:text-lg" data-testid="intro-body">
        {paragraphs(h.intro_body).map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </div>
    </Reveal>
  </section>
);

const ServicesOffered = ({ h, services }) => (
  <section id="services-offered" className="bg-white/60 py-16 sm:py-24" aria-labelledby="services-offered-title">
    <div className="wrap">
      <Reveal className="mb-12">
        <h2 id="services-offered-title" className="text-3xl text-[#22304A] sm:text-4xl" data-testid="services-offered-title">{h.services_title}</h2>
      </Reveal>
      <ServiceCards services={services} more={h.services_more} />
    </div>
  </section>
);

const AboutTeaser = ({ h }) => (
  <section className="wrap py-16 sm:py-24">
    <div className="grid items-center gap-12 md:grid-cols-[0.8fr_1.2fr] md:gap-20">
      <Reveal className="mx-auto w-full max-w-[320px]"><Portrait /></Reveal>
      <Reveal delay={150}>
        <p className="eyebrow mb-5">About</p>
        <p className="font-display text-2xl leading-snug text-[#22304A] sm:text-3xl" data-testid="about-teaser-text">{h.about_teaser}</p>
        <Link to="/about" className="btn-ghost mt-8" data-testid="about-teaser-link">{h.about_link} <ArrowRight className="h-4 w-4" aria-hidden="true" /></Link>
      </Reveal>
    </div>
  </section>
);

const FaqTeaser = ({ h }) => (
  <section className="bg-white/60 py-16 sm:py-24">
    <div className="wrap">
      <Reveal className="mb-10 flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <h2 className="text-3xl text-[#22304A] sm:text-4xl">Common questions</h2>
        <Link to="/faq" className="text-link text-[15px]" data-testid="faq-teaser-link">{h.faq_link}</Link>
      </Reveal>
      <dl className="grid gap-x-12 gap-y-10 md:grid-cols-2">
        {h.faq_teaser.map((f, i) => (
          <Reveal key={f.q} delay={i * 80} data-testid={`faq-teaser-item-${i}`}>
            <dt className="font-display text-2xl text-[#22304A]">{f.q}</dt>
            <dd className="mt-2 leading-relaxed text-[#22304A]/75">{f.a}</dd>
          </Reveal>
        ))}
      </dl>
    </div>
  </section>
);

export default function Home() {
  const { content } = useContent();
  const h = content.home;
  useMeta(
    "Registered Social Worker Toronto · Virtual therapy Ontario",
    "Online psychotherapy across Ontario with Violeta Dubov, Registered Social Worker in Toronto. Virtual therapy for adults (aged 18+). Book a free 20-minute phone intro."
  );
  return (
    <>
      <Hero h={h} />
      <div className="wrap"><div className="hairline" /></div>
      <Intro h={h} />
      <AboutTeaser h={h} />
      <ServicesOffered h={h} services={content.services.offered} />
      <FaqTeaser h={h} />
      <CtaBand title={h.final_cta_title} button={h.final_cta_button} />
    </>
  );
}
